// Header dropdowns + profile-modal language controls — owns
// renderLangControls / renderDirSummary / renderDiffDropdown /
// renderModeDropdown / switchGameMode plus the open/close helpers
// and all their event wiring.
//
// Bundled into one module because they share the "header dropdowns"
// responsibility (mutually-exclusive open/close via registerNestedClose)
// and the profile modal's lang/dir controls re-use the same
// LANG_NAMES / LANG_SHORT scaffolding as the wizard.

import {
  DIFFICULTY_MODES,
  DIFFICULTY_ORDER,
  GAME_MODES,
  GAME_MODE_ORDER,
  type DifficultyKey,
  type GameModeKey,
} from "../config/modes";
import {
  LANG_NAMES,
  LANG_NATIVE,
  LANG_SHORT,
  LANG_TO_LOCALE,
  LANGS,
  defaultTargetFor,
  type Lang,
  type Locale,
} from "../config/langs";
import { LIST_CATALOG, listsForTheme, type CardList } from "../config/lists";
import { THEMES } from "../config/themes";
import { STORAGE_KEYS } from "../config/storage-keys";
import {
  AXIS_TAGS,
  TAG_AXES,
  emptyTagFilter,
  tagFilterCount,
  type TagAxis,
  type TagFilter,
} from "../config/tags";
import {
  saveDifficulty,
  saveDirRatio,
  saveTagFilter,
} from "../state/settings";
import { currentLocale, t } from "../i18n";
import { translateTag } from "../i18n/tag-i18n";
import { registerNestedClose } from "./header-menu";
import { byId } from "./dom";
import { renderDirPresets } from "./dir-presets";

export type DropdownsDeps = {
  // AppState getters/setters — injected by bootstrap so this module
  // stays free of a direct state import.
  getDifficulty: () => DifficultyKey;
  setDifficulty: (d: DifficultyKey) => void;
  getGameMode: () => GameModeKey;
  setGameMode: (m: GameModeKey) => void;
  getTargetLang: () => Lang | null;
  setTargetLang: (lang: Lang) => void;
  getSourceLang: () => Lang | null;
  setSourceLang: (lang: Lang) => void;
  getDirRatio: () => number;
  setDirRatio: (r: number) => void;
  getTagFilter: () => TagFilter;
  setTagFilter: (f: TagFilter) => void;
  hasCards: () => boolean;
  /** Raw card list used by the filter popover to decide which axes are
   *  relevant (an axis with zero tagged cards in the current deck is
   *  hidden). Called at render time, not cached — switchList triggers
   *  a re-render once the new deck's cards are loaded. */
  getCards: () => readonly import("../types").Card[];
  hasCurrent: () => boolean;
  /** Reset transient state before switching modes (defi state + session
   *  counter). Called by switchGameMode. */
  resetModeTransients: () => void;
  /** Currently-active deck id (from config/lists). Used by the list
   *  dropdown to mark the selected entry. */
  getCurrentListId: () => string;
  /** Currently-active learning theme id (from config/themes). Filters
   *  which decks show in the deck dropdown. */
  getCurrentThemeId: () => string;
  /** Active deck — profile modal reads its `shape` to hide the
   *  lang-pair picker on mono decks (where both faces share the UI
   *  locale and the "J'apprends / Depuis" row is meaningless). */
  getList: () => import("../config/lists").CardList;
  /** Swap to another deck. The bootstrap wires this to switchList,
   *  which flushes sync, persists, and reloads the new CSV. Also
   *  syncs `currentThemeId` from the deck's `themeId` — the deck is
   *  the single source of truth for the active theme, so the tabbed
   *  deck picker doesn't need a separate switchTheme call. */
  switchList: (id: string) => void;

  // Cross-module callbacks — wired by bootstrap.
  knownLang: () => Lang;
  nextCard: () => void;
  startDefi: () => void;
  renderUserBtn: () => void;
  setLocale: (loc: string) => void;
};

export type Dropdowns = {
  renderLangControls: () => void;
  renderDirSummary: () => void;
  renderDiffDropdown: () => void;
  renderModeDropdown: () => void;
  renderTagFilterDropdown: () => void;
  renderListDropdown: () => void;
  switchGameMode: (mode: GameModeKey) => void;
  closeModeDropdown: () => void;
  closeListDropdown: () => void;
};

export function createDropdowns(deps: DropdownsDeps): Dropdowns {
  // Profile-modal form controls — captured at factory-init time; the
  // modal exists in the DOM from the start (hidden).
  const modalLearnSelect = byId<HTMLSelectElement>("modalLearnSelect");
  const modalSourceSelect = byId<HTMLSelectElement>("modalSourceSelect");
  const userModalCloseBtn = byId<HTMLButtonElement>("userModalCloseBtn");

  // Plain-language gloss centred under the slider, e.g. "10% de bulgare
  // vers français, 90% de français vers bulgare". The connector words
  // ("de" / "vers") are localized so each UI language reads naturally.
  function renderDirSummary(): void {
    const el = byId("modalDirSummary");
    if (!el) return;
    const src = deps.getSourceLang() || deps.knownLang();
    const tgt = deps.getTargetLang() || defaultTargetFor(currentLocale());
    const loc = currentLocale();
    const writeTarget = deps.getDirRatio(); // % source → target
    const readTarget = 100 - writeTarget;   // % target → source
    const srcName = LANG_NAMES[loc][src];
    const tgtName = LANG_NAMES[loc][tgt];
    const phrase = (pct: number, from: string, to: string): string =>
      `${pct}% ${t("dir_phrase_from")} ${from} ${t("dir_phrase_to")} ${to}`;
    const parts: string[] = [];
    if (readTarget > 0) parts.push(phrase(readTarget, tgtName, srcName));
    if (writeTarget > 0) parts.push(phrase(writeTarget, srcName, tgtName));
    el.textContent = parts.join(", ");
  }

  function renderLangControls(): void {
    const loc = currentLocale();
    // Lang-pair picker (target/source) only makes sense for bilingual
    // decks — mono decks put both faces in the UI locale, so the row
    // is meaningless there. Hide it and bail out before the select
    // initialisation so stale onchange handlers don't fire.
    const isMono = deps.getList().shape === "mono";
    const langPairRow = byId("userModalLangPairRow");
    langPairRow?.classList.toggle("hidden", isMono);
    // UI locale select — profile modal.
    const localeSelect = byId<HTMLSelectElement>("modalLocaleSelect");
    if (localeSelect) {
      localeSelect.innerHTML = LANGS.map((lang) => {
        const code = LANG_TO_LOCALE[lang];
        const sel = code === loc ? " selected" : "";
        return `<option value="${code}"${sel}>${LANG_NATIVE[lang]}</option>`;
      }).join("");
      localeSelect.onchange = () => {
        deps.setLocale(localeSelect.value);
      };
    }
    // User button name.
    deps.renderUserBtn();
    // Both selects auto-swap when the user picks a value that conflicts
    // with the other select (e.g. picking "bulgare" in both swaps them).
    if (modalLearnSelect) {
      modalLearnSelect.innerHTML = LANGS.map((lang) => {
        const sel = lang === deps.getTargetLang() ? " selected" : "";
        return `<option value="${lang}"${sel}>${LANG_NAMES[loc][lang]}</option>`;
      }).join("");
      modalLearnSelect.onchange = () => {
        const picked = modalLearnSelect.value as Lang;
        const src = deps.getSourceLang();
        if (picked === src) {
          // Swap: the old target becomes the source.
          const oldTarget = deps.getTargetLang();
          if (oldTarget) {
            deps.setSourceLang(oldTarget);
            localStorage.setItem(STORAGE_KEYS.SOURCE, oldTarget);
          }
        }
        deps.setTargetLang(picked);
        localStorage.setItem(STORAGE_KEYS.TARGET, picked);
        renderLangControls();
        if (deps.hasCurrent() && deps.getSourceLang()) deps.nextCard();
      };
    }
    if (modalSourceSelect) {
      modalSourceSelect.innerHTML = LANGS.map((lang) => {
        const sel = lang === deps.getSourceLang() ? " selected" : "";
        return `<option value="${lang}"${sel}>${LANG_NAMES[loc][lang]}</option>`;
      }).join("");
      modalSourceSelect.onchange = () => {
        const picked = modalSourceSelect.value as Lang;
        const tgt = deps.getTargetLang();
        if (picked === tgt) {
          // Swap: the old source becomes the target.
          const oldSource = deps.getSourceLang();
          if (oldSource) {
            deps.setTargetLang(oldSource);
            localStorage.setItem(STORAGE_KEYS.TARGET, oldSource);
          }
        }
        deps.setSourceLang(picked);
        localStorage.setItem(STORAGE_KEYS.SOURCE, picked);
        renderLangControls();
        if (deps.hasCurrent() && deps.getTargetLang()) deps.nextCard();
      };
    }
    // With selects, pair is always complete (auto-swap prevents conflicts).
    if (userModalCloseBtn) {
      userModalCloseBtn.disabled = false;
      userModalCloseBtn.title = "";
    }
    // Direction preset pills — replaces the old slider. Underlying
    // state is still a 0-100 ratio; the picker just constrains the
    // user's choice to 3 presets (read-biased / balanced / write-
    // biased). The "active" pill matches the nearest preset to the
    // stored value so imported settings still render sensibly.
    const dirPresetsHost = byId("modalDirPresets");
    if (dirPresetsHost) {
      // Face labels: bilingual decks use lang short codes (FR / BG);
      // mono decks use the face's own label (pays / capitale).
      const list = deps.getList();
      const faceLabel = (idx: 0 | 1): string => {
        const face = list.faces[idx];
        if (list.shape === "bilingual" && face.lang) {
          return LANG_SHORT[face.lang];
        }
        return String(t(face.labelKey));
      };
      renderDirPresets(
        dirPresetsHost,
        deps.getDirRatio(),
        (r) => {
          deps.setDirRatio(r);
          saveDirRatio(deps.getCurrentListId(), r);
          renderDirSummary();
          // Re-pick immediately so the new direction takes effect on
          // the NEXT visible card, not the one the user is currently
          // looking at (which was already picked with the old ratio).
          // Matches the behaviour of difficulty / filter pills in the
          // same modal — changing a deck setting should affect what
          // you see right away, not after you skip.
          if (deps.hasCards()) deps.nextCard();
        },
        [faceLabel(0), faceLabel(1)],
      );
    }
    const dirLabel = byId("modalDirLabel");
    if (dirLabel) {
      // Label order of preference: deck-specific override (e.g.
      // "Pays ou capitale ?" for capitales) → mono generic → bilingual
      // generic. Deck-specific keeps the phrasing grounded in what the
      // user is actually learning.
      const list = deps.getList();
      const labelKey =
        list.dirLabelKey ?? (isMono ? "dir_preset_label_mono" : "dir_preset_label");
      dirLabel.textContent = t(labelKey) as string;
    }
    renderDirSummary();
  }

  // -------- Difficulty pill row (inside deck settings modal) -----------
  // Used to be a hamburger dropdown (own button + popover). In 1b it
  // moved into the deck-settings modal as an always-open pill row —
  // the #diffDropdownMenu target div stays the same so this render
  // logic barely changed. Kept the function name to avoid churn in
  // callers; it's a "render the pills into the modal section" now.

  function renderDiffDropdown(): void {
    const d = deps.getDifficulty();
    const current: DifficultyKey = DIFFICULTY_MODES[d] ? d : "adaptive";
    const menu = byId("diffDropdownMenu");
    if (!menu) return;
    menu.innerHTML = DIFFICULTY_ORDER.map((mode) => {
      const active = mode === current ? " active" : "";
      return (
        `<button type="button" class="lang-dropdown-item${active}" data-mode="${mode}">` +
        `<span class="code">${DIFFICULTY_MODES[mode].short}</span>` +
        `<span>${t("diff_" + mode)}</span>` +
        `</button>`
      );
    }).join("");
    menu.querySelectorAll<HTMLButtonElement>(".lang-dropdown-item").forEach((btn) => {
      btn.addEventListener("click", () => {
        const m = btn.dataset.mode as DifficultyKey;
        deps.setDifficulty(m);
        saveDifficulty(deps.getCurrentListId(), m);
        renderDiffDropdown();
        // Re-pick a card under the new distribution so the change is
        // immediately visible — the modal stays open so the user can
        // tweak other settings and see them take effect right away.
        if (deps.hasCards()) deps.nextCard();
      });
    });
  }

  // -------- Game-mode dropdown (libre / défi / révision) --------------

  const modeDropdown = byId("modeDropdown");
  const modeDropdownBtn = byId<HTMLButtonElement>("modeDropdownBtn");

  function openModeDropdown(): void {
    modeDropdown?.classList.add("open");
  }
  function closeModeDropdown(): void {
    modeDropdown?.classList.remove("open");
  }

  // Wire dropdown closers into the header-menu module so the mobile ☰
  // menu's close also collapses any expanded dropdown underneath it.
  // Only the mode dropdown still lives in the header (difficulty +
  // filter moved to the deck-settings modal in 1b).
  registerNestedClose(closeModeDropdown);

  function renderModeDropdown(): void {
    const m = deps.getGameMode();
    const current: GameModeKey = GAME_MODES[m] ? m : "libre";
    const currentEl = byId("modeDropdownCurrent");
    if (currentEl) currentEl.textContent = GAME_MODES[current].icon;
    if (modeDropdownBtn) modeDropdownBtn.title = t("mode_title") as string;
    const menu = byId("modeDropdownMenu");
    if (!menu) return;
    menu.innerHTML = GAME_MODE_ORDER.map((mode) => {
      const active = mode === current ? " active" : "";
      return (
        `<button type="button" class="lang-dropdown-item${active}" data-mode="${mode}">` +
        `<span class="code">${GAME_MODES[mode].icon}</span>` +
        `<span>${t("mode_" + mode)}</span>` +
        `</button>`
      );
    }).join("");
    menu.querySelectorAll<HTMLButtonElement>(".lang-dropdown-item").forEach((btn) => {
      btn.addEventListener("click", () => {
        closeModeDropdown();
        switchGameMode(btn.dataset.mode as GameModeKey);
      });
    });
  }

  function switchGameMode(mode: GameModeKey): void {
    deps.setGameMode(mode);
    localStorage.setItem(STORAGE_KEYS.GAME_MODE, mode);
    deps.resetModeTransients();
    renderModeDropdown();
    if (mode === "defi") {
      deps.startDefi();
    } else if (deps.hasCards()) {
      deps.nextCard();
    }
  }

  if (modeDropdownBtn) {
    modeDropdownBtn.addEventListener("click", (e) => {
      e.stopPropagation();
      if (modeDropdown?.classList.contains("open")) closeModeDropdown();
      else openModeDropdown();
    });
  }
  document.addEventListener("click", (e) => {
    if (modeDropdown && !modeDropdown.contains(e.target as Node)) {
      closeModeDropdown();
    }
  });

  // -------- Tag filter (bilingual: class / domain / register — mono: auto-discovered tags) ----
  // Was a hamburger dropdown; moved into the deck-settings modal in
  // 1b. Rendering fills #tagFilterDropdownMenu. Commit 2 made it
  // deck-aware: bilingual decks use the 3 hardcoded axes (each with a
  // fixed vocabulary in CLASS_TAGS / DOMAIN_TAGS / REGISTER_TAGS);
  // mono decks build a single virtual axis from whatever tags the
  // current deck's cards actually carry (geography regions, etc.),
  // because CLASS_TAGS' verb/noun/… vocab is meaningless on them.
  //
  // Storage compat: mono selections slot into `filter.class` because
  // cardMatchesTagFilter treats filter[axis] as a free-form OR list,
  // not as "must be in CLASS_TAGS". Stored per-deck in the tagFilter
  // bag so bilingual decks keep their real class pills when you
  // switch back.

  /** Axes for which at least one card in the current deck carries a tag
   *  belonging to that axis (bilingual path). Always includes class as
   *  a baseline so the popover never appears fully empty. */
  function relevantAxes(): Set<TagAxis> {
    const out = new Set<TagAxis>(["class"]);
    const list = deps.getCards();
    const filter = deps.getTagFilter();
    for (const axis of TAG_AXES) {
      if (out.has(axis)) continue;
      // Always show an axis when the user has active pills in it —
      // hiding a filter they can't uncheck is a trap (silent narrowing).
      if (filter[axis].length > 0) {
        out.add(axis);
        continue;
      }
      // Otherwise show only when the current deck has at least one
      // card tagged with something from this axis.
      const vocab = AXIS_TAGS[axis] as readonly string[];
      for (const c of list) {
        if (c.tags.some((t) => vocab.includes(t))) {
          out.add(axis);
          break;
        }
      }
    }
    return out;
  }

  /** Axis view model: what to render, and which storage slot the pill
   *  writes to. For bilingual decks, one per relevant axis with its
   *  fixed vocabulary. For mono decks, one virtual axis whose vocab
   *  is the union of tags actually present in the loaded cards. */
  type AxisView = {
    storageAxis: TagAxis;
    labelKey: string | null; // null → no label (used for mono's single axis)
    tags: readonly string[];
  };

  function computeAxisViews(): AxisView[] {
    const list = deps.getList();
    if (list.shape === "mono") {
      // Auto-discover tags from the actual card set. Sort
      // alphabetically so the pill order is deterministic + readable;
      // dedupe via Set.
      const tagSet = new Set<string>();
      for (const c of deps.getCards()) {
        for (const tag of c.tags) tagSet.add(tag);
      }
      const tags = [...tagSet].sort();
      if (!tags.length) return [];
      return [{ storageAxis: "class", labelKey: null, tags }];
    }
    const shown = relevantAxes();
    const out: AxisView[] = [];
    for (const axis of TAG_AXES) {
      if (!shown.has(axis)) continue;
      out.push({
        storageAxis: axis,
        labelKey: `filter_axis_${axis}`,
        tags: AXIS_TAGS[axis] as readonly string[],
      });
    }
    return out;
  }

  function renderTagFilterDropdown(): void {
    const filter = deps.getTagFilter();
    const totalActive = tagFilterCount(filter);
    const loc = currentLocale();
    const menu = byId("tagFilterDropdownMenu");
    if (!menu) return;
    const axisViews = computeAxisViews();
    const sections = axisViews
      .map((view) => {
        const active = new Set(filter[view.storageAxis]);
        const pills = view.tags
          .map((tag) => {
            const on = active.has(tag) ? " active" : "";
            return (
              `<button type="button" class="lang-dropdown-item filter-pill${on}" data-axis="${view.storageAxis}" data-tag="${tag}">` +
              `<span>${translateTag(tag, loc)}</span>` +
              `</button>`
            );
          })
          .join("");
        const labelHtml = view.labelKey
          ? `<div class="filter-section-label">${t(view.labelKey)}</div>`
          : "";
        return (
          `<div class="filter-section" data-axis="${view.storageAxis}">` +
          labelHtml +
          `<div class="filter-section-pills">${pills}</div>` +
          `</div>`
        );
      })
      .join("");
    const resetDisabled = totalActive === 0 ? " disabled" : "";
    const resetRow =
      `<button type="button" class="lang-dropdown-item filter-reset"${resetDisabled} id="tagFilterResetBtn">` +
      `<span>${t("filter_class_reset")}</span>` +
      `</button>`;
    menu.innerHTML =
      `<div class="filter-sections">${sections}</div>` + resetRow;
    menu.querySelectorAll<HTMLButtonElement>(".filter-pill").forEach((btn) => {
      btn.addEventListener("click", (e) => {
        e.stopPropagation();
        const axis = btn.dataset.axis as TagAxis | undefined;
        const tag = btn.dataset.tag as string | undefined;
        if (!axis || !tag) return;
        const cur = deps.getTagFilter();
        // Copy-on-write: don't mutate the state slice directly so the
        // setTagFilter callback sees a distinct reference and can trigger
        // change detection if it wants.
        const next: TagFilter = {
          class: [...cur.class],
          domain: [...cur.domain],
          register: [...cur.register],
        };
        const idx = next[axis].indexOf(tag);
        if (idx >= 0) next[axis].splice(idx, 1);
        else next[axis].push(tag);
        deps.setTagFilter(next);
        saveTagFilter(deps.getCurrentListId(), next);
        renderTagFilterDropdown();
        // Re-pick immediately so the user sees the effect without
        // manually skipping.
        if (deps.hasCards()) deps.nextCard();
      });
    });
    const resetBtn = menu.querySelector<HTMLButtonElement>("#tagFilterResetBtn");
    resetBtn?.addEventListener("click", (e) => {
      e.stopPropagation();
      if (tagFilterCount(deps.getTagFilter()) === 0) return;
      const empty = emptyTagFilter();
      deps.setTagFilter(empty);
      saveTagFilter(deps.getCurrentListId(), empty);
      renderTagFilterDropdown();
      if (deps.hasCards()) deps.nextCard();
    });
  }

  // (No open/close wiring — the filter section lives inside the
  // deck-settings modal now; opening the modal re-renders the pills
  // via deckSettings.open() calling renderTagFilterDropdown().)

  // -------- List (deck) dropdown -------------------------------------

  const listDropdown = byId("listDropdown");
  const listDropdownBtn = byId<HTMLButtonElement>("listDropdownBtn");

  function openListDropdown(): void {
    listDropdown?.classList.add("open");
  }
  function closeListDropdown(): void {
    listDropdown?.classList.remove("open");
  }
  registerNestedClose(closeListDropdown);

  // Local "tab being browsed" — separate from `currentThemeId` (which
  // is the committed theme). Lets the user explore other themes' decks
  // without committing a theme switch until they actually pick a deck.
  // Reset to currentThemeId every time the dropdown opens (below in
  // openListDropdown), then mutated by tab clicks.
  let listActiveTab: string | null = null;

  function renderListDropdown(): void {
    // Single dropdown that contains the whole catalog, grouped by
    // theme via a row of tab pills at the top. Hide the whole control
    // when the catalog has only one deck — nothing to switch to.
    if (!listDropdown) return;
    if (LIST_CATALOG.length < 2) {
      listDropdown.classList.add("hidden");
      return;
    }
    listDropdown.classList.remove("hidden");

    const currentListId = deps.getCurrentListId();
    const currentList: CardList | undefined =
      LIST_CATALOG.find((l) => l.id === currentListId) ?? LIST_CATALOG[0];
    const currentEl = byId("listDropdownCurrent");
    if (currentEl && currentList) {
      currentEl.textContent = t(currentList.nameKey) as string;
    }
    if (listDropdownBtn) listDropdownBtn.title = t("list_title") as string;
    const overflowLabel = byId("listOverflowLabel");
    if (overflowLabel) {
      overflowLabel.textContent = t("list_overflow_label") as string;
    }

    const menu = byId("listDropdownMenu");
    if (!menu) return;

    // Active tab defaults to the committed theme. Persists across
    // re-renders triggered by tab clicks; reset on each open() so a
    // closed-then-reopened dropdown lands back on the user's actual
    // current theme.
    const activeTab = listActiveTab ?? deps.getCurrentThemeId();

    // Tabs row — only when ≥2 themes. Tab order = THEMES catalog order.
    const tabsHtml =
      THEMES.length > 1
        ? `<div class="list-dropdown-tabs">${THEMES.map((theme) => {
            const cls = theme.id === activeTab ? " active" : "";
            return (
              `<button type="button" class="list-dropdown-tab${cls}" data-theme-id="${theme.id}">` +
              `<span class="code">${theme.icon}</span>` +
              `<span>${t(theme.nameKey)}</span>` +
              `</button>`
            );
          }).join("")}</div>`
        : "";

    // Decks list for the active tab — same item style as before so
    // nothing else changes visually. Active deck highlighted only when
    // the user is browsing its theme's tab.
    const decks = listsForTheme(activeTab);
    const decksHtml = `<div class="list-dropdown-decks">${decks
      .map((list) => {
        const active = list.id === currentListId ? " active" : "";
        return (
          `<button type="button" class="lang-dropdown-item${active}" data-list-id="${list.id}">` +
          `<span>${t(list.nameKey)}</span>` +
          `</button>`
        );
      })
      .join("")}</div>`;

    menu.innerHTML = tabsHtml + decksHtml;

    // Tab click → swap the visible decks list without closing the
    // dropdown or committing a theme switch.
    menu.querySelectorAll<HTMLButtonElement>(".list-dropdown-tab").forEach((btn) => {
      btn.addEventListener("click", (e) => {
        e.stopPropagation();
        const themeId = btn.dataset.themeId;
        if (themeId && themeId !== activeTab) {
          listActiveTab = themeId;
          renderListDropdown();
        }
      });
    });

    // Deck click → real switch. switchList syncs currentThemeId from
    // the deck's themeId, so picking a deck in another tab also
    // commits the theme change.
    menu.querySelectorAll<HTMLButtonElement>(".lang-dropdown-item").forEach((btn) => {
      btn.addEventListener("click", () => {
        closeListDropdown();
        const id = btn.dataset.listId;
        if (id && id !== deps.getCurrentListId()) deps.switchList(id);
      });
    });
  }

  if (listDropdownBtn) {
    listDropdownBtn.addEventListener("click", (e) => {
      e.stopPropagation();
      closeModeDropdown();
      if (listDropdown?.classList.contains("open")) {
        closeListDropdown();
      } else {
        // Reset the local "tab being browsed" to the committed theme
        // each time the dropdown opens — a closed-then-reopened picker
        // shouldn't remember the user's last hovered tab.
        listActiveTab = deps.getCurrentThemeId();
        renderListDropdown();
        openListDropdown();
      }
    });
  }
  document.addEventListener("click", (e) => {
    if (listDropdown && !listDropdown.contains(e.target as Node)) {
      closeListDropdown();
    }
  });

  return {
    renderLangControls,
    renderDirSummary,
    renderDiffDropdown,
    renderModeDropdown,
    renderTagFilterDropdown,
    renderListDropdown,
    switchGameMode,
    closeModeDropdown,
    closeListDropdown,
  };
}
